/**
 * Cross-tab auth synchronization.
 *
 * Why this exists: `useAuthQuery` opts out of `refetchOnWindowFocus` to
 * avoid hammering `/api/auth/me` on every tab-switch (identity rarely
 * changes mid-session). That optimization used to leave sibling tabs
 * stale when impersonation started/stopped in one tab. This module is
 * the mitigation — a lightweight BroadcastChannel that sibling tabs
 * listen to so they can invalidate `authKeys.me` on demand.
 *
 * Design notes:
 *  - SSR safe: every call is guarded on `typeof window` and a feature
 *    check for `BroadcastChannel`. If the browser lacks support, we
 *    silently degrade (the tab simply doesn't sync — no worse than the
 *    pre-mitigation behavior).
 *  - BroadcastChannel does NOT deliver messages to the tab that posted
 *    them, so local consumers don't need to de-dupe.
 *  - The channel is intentionally single-purpose. Do not reuse it for
 *    non-auth concerns — that would couple unrelated features to a
 *    single message bus.
 */

const CHANNEL_NAME = "dawebapp-auth";

export type AuthBroadcastMessage =
  | { type: "impersonation:start"; clientId: string }
  | { type: "impersonation:stop" }
  | { type: "logout" };

function getChannel(): BroadcastChannel | null {
  if (typeof window === "undefined") return null;
  if (typeof BroadcastChannel === "undefined") return null;
  return new BroadcastChannel(CHANNEL_NAME);
}

/**
 * Broadcast an auth event to sibling tabs. No-op on the server or in
 * browsers without BroadcastChannel support.
 */
export function broadcastAuthEvent(message: AuthBroadcastMessage): void {
  const channel = getChannel();
  if (!channel) return;
  try {
    channel.postMessage(message);
  } finally {
    channel.close();
  }
}

/**
 * Subscribe to auth events from sibling tabs. Returns an unsubscribe
 * function. No-op (returns a no-op unsubscribe) on the server or in
 * browsers without BroadcastChannel support.
 */
export function subscribeToAuthEvents(
  handler: (message: AuthBroadcastMessage) => void,
): () => void {
  const channel = getChannel();
  if (!channel) return () => {};

  const listener = (event: MessageEvent<AuthBroadcastMessage>) => {
    handler(event.data);
  };

  channel.addEventListener("message", listener);
  return () => {
    channel.removeEventListener("message", listener);
    channel.close();
  };
}
